import { supabase } from '../supabase';
import type { GroupWithMemberCount } from '../types/database';
import type { CreateGroupInput, UpdateGroupInput } from '../types/groups.types';

export type { GroupWithMemberCount };

function getRpcErrorMessage(errorMessage: string): string {
  const cleanMessage = errorMessage.replace(/^ERROR:\s*/i, '').trim();
  return cleanMessage || 'Group request failed.';
}

export async function listGroups(
  includeArchived = false
): Promise<GroupWithMemberCount[]> {
  const { data, error } = await supabase.rpc('list_groups', {
    p_include_archived: includeArchived,
  });

  if (error) {
    throw new Error(
      `Failed to list groups: ${getRpcErrorMessage(error.message)}`
    );
  }

  return (data ?? []) as GroupWithMemberCount[];
}

export async function createGroup(
  input: CreateGroupInput
): Promise<GroupWithMemberCount> {
  const { data, error } = await supabase.rpc('create_group', {
    p_display_name: input.displayName,
    p_parent_role: input.parentRole,
    p_description: input.description ?? null,
  });

  if (error) {
    throw new Error(getRpcErrorMessage(error.message));
  }

  const group = (data as GroupWithMemberCount[] | null)?.[0];

  if (!group) {
    throw new Error('Group was not created.');
  }

  return group;
}

export async function updateGroup(
  input: UpdateGroupInput
): Promise<GroupWithMemberCount> {
  const { data, error } = await supabase.rpc('update_group', {
    p_group_id: input.groupId,
    p_display_name: input.displayName,
    p_parent_role: input.parentRole,
    p_description: input.description ?? null,
  });

  if (error) {
    throw new Error(getRpcErrorMessage(error.message));
  }

  const group = (data as GroupWithMemberCount[] | null)?.[0];

  if (!group) {
    throw new Error('Group was not updated.');
  }

  return group;
}

export async function archiveGroup(
  groupId: string
): Promise<GroupWithMemberCount> {
  const { data, error } = await supabase.rpc('archive_group', {
    p_group_id: groupId,
  });

  if (error) {
    throw new Error(getRpcErrorMessage(error.message));
  }

  const group = (data as GroupWithMemberCount[] | null)?.[0];

  if (!group) {
    throw new Error('Group was not archived.');
  }

  return group;
}

export async function restoreGroup(
  groupId: string
): Promise<GroupWithMemberCount> {
  const { data, error } = await supabase.rpc('restore_group', {
    p_group_id: groupId,
  });

  if (error) {
    throw new Error(getRpcErrorMessage(error.message));
  }

  const group = (data as GroupWithMemberCount[] | null)?.[0];

  if (!group) {
    throw new Error('Group was not restored.');
  }

  return group;
}

export async function getGroup(
  groupId: string
): Promise<GroupWithMemberCount | null> {
  const groups = await listGroups(true);
  return groups.find((g) => g.id === groupId) ?? null;
}
